// pages/EcfConfiguracion.jsx
// Página de configuración e-CF (Alanube / DGII)

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import Swal from 'sweetalert2';

const EcfConfiguracion = () => {
  const navigate = useNavigate();

  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [totalLogs, setTotalLogs] = useState(0);
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [probando, setProbando] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [paginaLogs, setPaginaLogs] = useState(1);

  // ── Selector de empresa ───────────────────────────────────────────
  const [empresas, setEmpresas] = useState([]);
  const [empresaSelId, setEmpresaSelId] = useState(
    () => localStorage.getItem('empresa_contable_id') || ''
  );

  const [form, setForm] = useState({
    habilitado: false,
    ambiente: 'prueba',
    envio_async: true,
    reintentos: 3,
    timeout: 10
  });

  // Carga lista de empresas una sola vez
  useEffect(() => {
    api.get('/empresas-contables')
      .then(res => {
        const lista = res.data?.empresas || res.data || [];
        setEmpresas(lista);
        // Si no hay empresa seleccionada y hay exactamente una, seleccionarla
        if (!localStorage.getItem('empresa_contable_id') && lista.length === 1) {
          cambiarEmpresa(String(lista[0].id));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    cargarTodo();
  }, [empresaSelId]);

  useEffect(() => {
    cargarLogs();
  }, [filtroEstado, paginaLogs]);

  const cambiarEmpresa = (nuevoId) => {
    if (nuevoId) {
      localStorage.setItem('empresa_contable_id', nuevoId);
      const emp = empresas.find(e => String(e.id) === String(nuevoId));
      if (emp) localStorage.setItem('empresa_contable_nombre', emp.nombre);
    } else {
      localStorage.removeItem('empresa_contable_id');
      localStorage.removeItem('empresa_contable_nombre');
    }
    setEmpresaSelId(nuevoId);
    setPaginaLogs(1);
  };

  const cargarTodo = async () => {
    try {
      setLoading(true);
      const [resConfig, resStats] = await Promise.all([
        api.get('/ecf/config'),
        api.get('/ecf/stats')
      ]);
      const c = resConfig.data.configuracion;
      setConfig(c);
      setForm({
        habilitado: c.habilitado || false,
        ambiente: c.ambiente || 'prueba',
        envio_async: c.envio_async !== false,
        reintentos: c.reintentos || 3,
        timeout: Math.round((c.timeout || 10000) / 1000)
      });
      setStats(resStats.data.estadisticas);
    } catch (error) {
      console.error('Error cargando config e-CF:', error);
      Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo cargar la configuración e-CF' });
    } finally {
      setLoading(false);
    }
  };

  const cargarLogs = async () => {
    try {
      const params = { page: paginaLogs, limit: 10 };
      if (filtroEstado) params.estado = filtroEstado;
      const res = await api.get('/ecf/logs', { params });
      setLogs(res.data.logs || []);
      setTotalLogs(res.data.total || 0);
    } catch (error) {
      console.error('Error cargando logs e-CF:', error);
    }
  };

  const handleGuardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      const datos = {
        habilitado: form.habilitado,
        ambiente: form.ambiente,
        envio_async: form.envio_async,
        reintentos: parseInt(form.reintentos),
        timeout: parseInt(form.timeout) * 1000
      };

      await api.put('/ecf/config', datos);
      await Swal.fire({ icon: 'success', title: '¡Guardado!', text: 'Configuración e-CF actualizada' });
      cargarTodo();
    } catch (error) {
      const msg = error.response?.data?.detalle || error.response?.data?.error || 'Error al guardar';
      Swal.fire({ icon: 'error', title: 'Error', text: msg });
    } finally {
      setGuardando(false);
    }
  };

  const handleProbarConexion = async () => {
    setProbando(true);
    try {
      const res = await api.post('/ecf/config/test');
      const r = res.data.resultado;
      if (r.success) {
        Swal.fire({ icon: 'success', title: 'Conexión exitosa', text: 'API Key válida — Alanube respondió correctamente' });
      } else {
        Swal.fire({ icon: 'error', title: 'Falló la conexión', text: r.error || 'No se pudo conectar con Alanube' });
      }
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: error.response?.data?.error || 'Error al probar conexión' });
    } finally {
      setProbando(false);
    }
  };

  const handleResetStats = async () => {
    const { isConfirmed } = await Swal.fire({
      icon: 'question',
      title: 'Resetear estadísticas',
      text: '¿Resetear los contadores de e-CF a cero?',
      showCancelButton: true,
      confirmButtonText: 'Resetear',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc3545'
    });
    if (!isConfirmed) return;
    try {
      await api.post('/ecf/stats/reset');
      const res = await api.get('/ecf/stats');
      setStats(res.data.estadisticas);
      Swal.fire({ icon: 'success', title: 'Reseteado', text: 'Estadísticas e-CF reiniciadas' });
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudieron resetear las estadísticas' });
    }
  };

  const handleReenviar = async (facturaId) => {
    const { isConfirmed } = await Swal.fire({
      icon: 'question',
      title: 'Reenviar e-CF',
      text: `¿Reenviar el e-CF de la Factura #${facturaId} a la DGII?`,
      showCancelButton: true,
      confirmButtonText: 'Reenviar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#0066cc'
    });
    if (!isConfirmed) return;
    try {
      await api.post(`/ecf/${facturaId}/reenviar`);
      await Swal.fire({ icon: 'success', title: '¡Reenviado!', text: 'e-CF reenviado correctamente' });
      cargarLogs();
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: error.response?.data?.detalle || 'Error al reenviar e-CF' });
    }
  };

  const getBadgeEstado = (estado) => {
    const map = {
      pendiente: { bg: '#f59e0b', label: 'Pendiente' },
      aceptado: { bg: '#28a745', label: 'Aceptado' },
      rechazado: { bg: '#dc3545', label: 'Rechazado' },
      contingencia: { bg: '#6c757d', label: 'Contingencia' },
      error: { bg: '#dc3545', label: 'Error' }
    };
    return map[estado] || { bg: '#6c757d', label: estado };
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p>Cargando configuración e-CF...</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '30px 20px' }}>
      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
        <button
          onClick={() => navigate('/configuracion')}
          style={{
            padding: '8px 16px', background: '#6c757d', color: '#fff',
            border: 'none', borderRadius: '6px', cursor: 'pointer'
          }}
        >
          ← Volver
        </button>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', color: '#1a1a2e' }}>
            📡 Configuración e-CF (Alanube / DGII)
          </h1>
          <p style={{ margin: '4px 0 0', color: '#666', fontSize: '14px' }}>
            Comprobantes Fiscales Electrónicos — República Dominicana
          </p>
        </div>
        <span style={{
          marginLeft: 'auto', padding: '4px 12px', borderRadius: '20px', fontSize: '13px',
          fontWeight: '600',
          background: config?.habilitado ? '#d4edda' : '#f8d7da',
          color: config?.habilitado ? '#155724' : '#721c24'
        }}>
          {config?.habilitado ? '● Activo' : '○ Inactivo'}
        </span>
      </div>

      {/* ── Selector de empresa contable ── */}
      {empresas.length > 0 && (
        <div style={{
          background: '#f0f4ff', border: '1px solid #c7d4f0', borderRadius: '10px',
          padding: '14px 18px', marginBottom: '24px',
          display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap'
        }}>
          <span style={{ fontWeight: '700', color: '#3730a3', fontSize: '14px', whiteSpace: 'nowrap' }}>
            🏢 Empresa:
          </span>
          <select
            value={empresaSelId}
            onChange={e => cambiarEmpresa(e.target.value)}
            style={{
              flex: 1, minWidth: '220px', padding: '8px 12px',
              border: '1.5px solid #818cf8', borderRadius: '6px',
              fontSize: '14px', background: '#fff', color: '#1e293b',
              fontWeight: '600', cursor: 'pointer'
            }}
          >
            <option value="">— Configuración general del tenant —</option>
            {empresas.map(e => (
              <option key={e.id} value={String(e.id)}>
                {e.nombre}{e.rnc ? ` · RNC ${e.rnc}` : ' · Sin RNC configurado'}
              </option>
            ))}
          </select>
          {empresaSelId && (() => {
            const emp = empresas.find(e => String(e.id) === String(empresaSelId));
            return emp && !emp.rnc ? (
              <span style={{
                padding: '4px 12px', background: '#fff3cd', border: '1px solid #ffc107',
                borderRadius: '20px', fontSize: '12px', color: '#856404', fontWeight: '600'
              }}>
                ⚠️ Esta empresa no tiene RNC — configúralo antes de habilitar e-CF
              </span>
            ) : null;
          })()}
        </div>
      )}

      <form onSubmit={handleGuardar}>
        {/* ── Sección 1: Estado ── */}
        <div style={cardStyle}>
          <h3 style={sectionTitleStyle}>Estado del módulo</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={form.habilitado}
                onChange={(e) => setForm({ ...form, habilitado: e.target.checked })}
                style={{ width: '18px', height: '18px' }}
              />
              <span style={{ fontWeight: '600' }}>Habilitar envío de e-CF a DGII vía Alanube</span>
            </label>
            <span style={{
              padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600',
              background: form.ambiente === 'produccion' ? '#fff3cd' : '#d1ecf1',
              color: form.ambiente === 'produccion' ? '#856404' : '#0c5460'
            }}>
              {form.ambiente === 'produccion' ? 'PRODUCCIÓN' : 'SANDBOX / PRUEBA'}
            </span>
          </div>
        </div>

        {/* ── Sección 2: Credenciales Alanube ── */}
        <div style={cardStyle}>
          <h3 style={sectionTitleStyle}>Credenciales Alanube</h3>

          <div style={{
            marginBottom: '16px', padding: '12px 14px',
            background: '#e8f4fd', border: '1px solid #bee3f8', borderRadius: '6px',
            fontSize: '13px', color: '#2c5282'
          }}>
            <strong>API Key (Reseller Key):</strong> configurada globalmente en el servidor (<code>.env</code>).
            El API Key identifica a eFIT como reseller ante Alanube — es una sola clave para todos los tenants.
            Para actualizarla, contacta al administrador del sistema.
            <div style={{ marginTop: '8px' }}>
              <button
                type="button"
                onClick={handleProbarConexion}
                disabled={probando}
                style={{
                  padding: '8px 14px', background: '#17a2b8', color: '#fff',
                  border: 'none', borderRadius: '6px', cursor: 'pointer',
                  opacity: probando ? 0.7 : 1, fontSize: '13px'
                }}
              >
                {probando ? 'Probando...' : 'Probar conexión con Alanube'}
              </button>
            </div>
          </div>

          <div>
            <label style={labelStyle}>Ambiente</label>
            <select
              value={form.ambiente}
              onChange={(e) => setForm({ ...form, ambiente: e.target.value })}
              style={inputStyle}
            >
              <option value="prueba">Sandbox / Pruebas (sandbox-api.alanube.co/dom/v1)</option>
              <option value="produccion">Producción (api.alanube.co/dom/v1)</option>
            </select>
          </div>
        </div>

        {/* ── Sección 3: Opciones ── */}
        <div style={cardStyle}>
          <h3 style={sectionTitleStyle}>Opciones de envío</h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div>
              <label style={labelStyle}>Reintentos automáticos (1–5)</label>
              <input
                type="number"
                min="1" max="5"
                value={form.reintentos}
                onChange={(e) => setForm({ ...form, reintentos: e.target.value })}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Timeout (segundos)</label>
              <input
                type="number"
                min="3" max="60"
                value={form.timeout}
                onChange={(e) => setForm({ ...form, timeout: e.target.value })}
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{ marginTop: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={form.envio_async}
                onChange={(e) => setForm({ ...form, envio_async: e.target.checked })}
                style={{ width: '16px', height: '16px' }}
              />
              <span>Envío asíncrono (no bloquea la respuesta al emitir factura)</span>
            </label>
          </div>
        </div>

        {/* ── Botón guardar ── */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '32px' }}>
          <button
            type="submit"
            disabled={guardando}
            style={{
              padding: '12px 32px', background: '#0066cc', color: '#fff',
              border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '15px',
              fontWeight: '600', opacity: guardando ? 0.7 : 1
            }}
          >
            {guardando ? 'Guardando...' : 'Guardar configuración'}
          </button>
        </div>
      </form>

      {/* ── Sección 4: Estadísticas ── */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <h3 style={{ margin: 0, fontSize: '18px' }}>Estadísticas en tiempo real</h3>
          <button
            onClick={handleResetStats}
            style={{
              padding: '6px 16px', background: '#dc3545', color: '#fff',
              border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px'
            }}
          >
            Resetear
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
          {[
            { label: 'Enviados', value: stats?.enviados ?? 0, color: '#0066cc' },
            { label: 'Aceptados', value: stats?.aceptados ?? 0, color: '#28a745' },
            { label: 'Rechazados', value: stats?.rechazados ?? 0, color: '#dc3545' },
            { label: 'Pendientes', value: stats?.pendientes ?? 0, color: '#f59e0b' }
          ].map(({ label, value, color }) => (
            <div key={label} style={{
              background: '#f8f9fa', borderRadius: '10px', padding: '20px',
              textAlign: 'center', borderTop: `4px solid ${color}`
            }}>
              <div style={{ fontSize: '32px', fontWeight: '700', color }}>{value}</div>
              <div style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>{label}</div>
            </div>
          ))}
        </div>

        {stats?.lastError && (
          <div style={{
            marginTop: '16px', padding: '10px 16px',
            background: '#f8d7da', borderRadius: '6px', color: '#721c24', fontSize: '13px'
          }}>
            Último error: {stats.lastError}
          </div>
        )}
      </div>

      {/* ── Sección 5: Logs recientes ── */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
          <h3 style={{ margin: 0, fontSize: '18px' }}>Log de e-CF recientes</h3>
          <select
            value={filtroEstado}
            onChange={(e) => { setFiltroEstado(e.target.value); setPaginaLogs(1); }}
            style={{ padding: '6px 12px', border: '1px solid #dee2e6', borderRadius: '6px', fontSize: '14px' }}
          >
            <option value="">Todos los estados</option>
            <option value="pendiente">Pendiente</option>
            <option value="aceptado">Aceptado</option>
            <option value="rechazado">Rechazado</option>
            <option value="error">Error</option>
            <option value="contingencia">Contingencia</option>
          </select>
          <span style={{ marginLeft: 'auto', color: '#666', fontSize: '13px' }}>
            {totalLogs} registros
          </span>
        </div>

        {logs.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
            No hay logs de e-CF registrados
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ background: '#f8f9fa' }}>
                  <th style={thStyle}>NCF</th>
                  <th style={thStyle}>Documento</th>
                  <th style={thStyle}>Estado</th>
                  <th style={thStyle}>Track ID</th>
                  <th style={thStyle}>Código DGII</th>
                  <th style={thStyle}>Intento</th>
                  <th style={thStyle}>Fecha</th>
                  <th style={thStyle}>Acción</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => {
                  const badge = getBadgeEstado(log.estado);
                  return (
                    <tr key={log.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={tdStyle}>{log.ncf || '—'}</td>
                      <td style={tdStyle}>
                        {log.FacturaId ? (
                          <a
                            href={`/facturas/${log.FacturaId}`}
                            style={{
                              display: 'inline-block',
                              padding: '3px 8px', borderRadius: '12px',
                              background: '#e8f0fe', color: '#0066cc',
                              fontSize: '12px', fontWeight: '600', textDecoration: 'none'
                            }}
                          >
                            #{log.factura?.numero_factura || log.FacturaId}
                          </a>
                        ) : log.VentaId ? (
                          <span style={{
                            display: 'inline-block',
                            padding: '3px 8px', borderRadius: '12px',
                            background: '#d4edda', color: '#155724',
                            fontSize: '12px', fontWeight: '600'
                          }}>
                            {log.venta?.numero_venta || `VTA-${log.VentaId}`}
                          </span>
                        ) : (
                          <span
                            title="Registro sin documento asociado"
                            style={{
                              display: 'inline-block',
                              padding: '3px 8px', borderRadius: '12px',
                              background: '#e2e3e5', color: '#6c757d',
                              fontSize: '12px', fontWeight: '600', cursor: 'default'
                            }}
                          >
                            SIN DOC
                          </span>
                        )}
                      </td>
                      <td style={tdStyle}>
                        <span style={{
                          padding: '3px 8px', borderRadius: '12px',
                          background: badge.bg, color: '#fff', fontSize: '12px', fontWeight: '600'
                        }}>
                          {badge.label}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {log.track_id ? (
                          <span title={log.track_id} style={{ fontSize: '12px', fontFamily: 'monospace' }}>
                            {log.track_id.slice(0, 16)}…
                          </span>
                        ) : '—'}
                      </td>
                      <td style={tdStyle}>{log.codigo_aceptacion || '—'}</td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>{log.intento}</td>
                      <td style={tdStyle}>
                        {new Date(log.createdAt).toLocaleDateString('es-DO', {
                          day: '2-digit', month: '2-digit', year: 'numeric'
                        })}
                      </td>
                      <td style={tdStyle}>
                        {(log.estado === 'error' || log.estado === 'rechazado') && (
                          <button
                            onClick={() => handleReenviar(log.FacturaId)}
                            style={{
                              padding: '4px 10px', background: '#0066cc', color: '#fff',
                              border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px'
                            }}
                          >
                            Reenviar
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Paginación */}
            {totalLogs > 10 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
                <button
                  disabled={paginaLogs === 1}
                  onClick={() => setPaginaLogs(p => p - 1)}
                  style={btnPageStyle}
                >
                  ← Anterior
                </button>
                <span style={{ padding: '6px 12px', color: '#666' }}>
                  Pág. {paginaLogs} / {Math.ceil(totalLogs / 10)}
                </span>
                <button
                  disabled={paginaLogs >= Math.ceil(totalLogs / 10)}
                  onClick={() => setPaginaLogs(p => p + 1)}
                  style={btnPageStyle}
                >
                  Siguiente →
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Estilos inline ──
const cardStyle = {
  background: '#fff',
  border: '1px solid #e9ecef',
  borderRadius: '12px',
  padding: '24px',
  marginBottom: '24px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
};

const sectionTitleStyle = {
  margin: '0 0 20px',
  fontSize: '18px',
  color: '#1a1a2e'
};

const labelStyle = {
  display: 'block',
  marginBottom: '6px',
  fontWeight: '600',
  color: '#495057',
  fontSize: '14px'
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #dee2e6',
  borderRadius: '6px',
  fontSize: '14px',
  boxSizing: 'border-box'
};

const thStyle = {
  padding: '10px 12px',
  textAlign: 'left',
  fontWeight: '600',
  color: '#495057',
  fontSize: '13px',
  borderBottom: '2px solid #dee2e6'
};

const tdStyle = {
  padding: '10px 12px',
  color: '#333'
};

const btnPageStyle = {
  padding: '6px 14px',
  background: '#0066cc',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '13px',
  opacity: 1
};

export default EcfConfiguracion;
